import { useEffect, useRef, useState } from "react";
import { Input, Radio } from "antd";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IAppointment } from "@base/modules/appointment/appointment.interface";
import appointmentService from "@base/modules/appointment/appointmentService";
import { isCancelLimitReached } from "@base/modules/appointment/appointmentDetailRules";
import { getAppointmentUsedService } from "@base/modules/appointment/appointmentUsedService";
import { baseRoutes } from "@base/routes/baseRoutes";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import CommonStyles from "@components/CommonStyles";

const MAX_REASON_LENGTH = 254;

type TReason = "reason1" | "reason2" | "reason3";

interface IProps {
  open: boolean;
  appointment: IAppointment;
  onClose: () => void;
}

/**
 * Cancelling: a confirmation, then the reason. The two set reasons are sent as their text in the
 * running language; "other" sends only what the patient typed.
 */
export default function ModalAppointmentCancelling({ open = false, appointment, onClose }: IProps) {
  //! state
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step, setStep] = useState<"confirm" | "reason">("confirm");
  const [reason, setReason] = useState<TReason>();
  const [note, setNote] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isSendingRef = useRef<boolean>(false);

  //! function
  useEffect(() => {
    if (open) {
      setStep("confirm");
      setReason(undefined);
      setNote("");
    }
  }, [open]);

  const canSubmit = reason === "reason1" || reason === "reason2" || (reason === "reason3" && !!note.trim());

  // Clicking the chosen reason again clears it.
  const onPick = (value: TReason) => setReason((current) => (current === value ? undefined : value));

  const onSubmit = async () => {
    if (!canSubmit || !reason || isSendingRef.current) {
      return;
    }

    const cancelReason = reason === "reason3" ? note.trim() : t(`appointmentDetail.cancelReason${reason.slice(-1)}`);

    try {
      isSendingRef.current = true;
      setIsLoading(true);
      await appointmentService.setCancelled(appointment.id, cancelReason);
      dispatch(setAlertMessage({ type: "success", message: t("Notification"), description: t("Successfully") + "!" }));
      onClose();
      navigate(baseRoutes.userAppointmentList);
    } catch (error) {
      const maxCancelTimes = getAppointmentUsedService(appointment).maxCancelTimes;
      const description = isCancelLimitReached((error as AxiosError)?.response?.data)
        ? maxCancelTimes
          ? t("packageBooking.cancelLimitReached", { times: maxCancelTimes })
          : t("packageBooking.cancelLimitReachedNoCount")
        : t("Failed") + "!";

      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description }));
    } finally {
      isSendingRef.current = false;
      setIsLoading(false);
    }
  };

  //! render
  if (step === "confirm") {
    return (
      <CommonStyles.ModalConfirmationUI
        open={open}
        toggle={onClose}
        type="normal"
        width={440}
        title={t("appointmentDetail.cancelConfirmTitle")}
        cancelText={t("Close")}
        okText={t("appointmentDetail.agree")}
        onConfirm={() => setStep("reason")}
        content={<span className="text-sm lg:text-base font-['Inter'] text-common-maintext">{t("appointmentDetail.cancelConfirmMessage")}</span>}
      />
    );
  }

  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={onClose}
      type="normal"
      width={560}
      title={t("appointmentDetail.cancelReasonTitle")}
      cancelText={t("Close")}
      okText={t("appointmentDetail.agree")}
      okButtonProps={{ disabled: !canSubmit, loading: isLoading }}
      onConfirm={onSubmit}
      content={
        <div className="flex flex-col gap-4">
          <div className="p-3 rounded-lg bg-common-primary50 text-sm font-['Inter'] text-common-primary">{t("appointmentDetail.cancelReasonHeader")}</div>

          <div className="flex flex-col gap-3">
            {(["reason1", "reason2", "reason3"] as TReason[]).map((value) => (
              <Radio key={value} checked={reason === value} onClick={() => onPick(value)}>
                <span className="text-sm lg:text-base font-['Inter'] text-common-maintext">{t(`appointmentDetail.cancelReason${value.slice(-1)}`)}</span>
              </Radio>
            ))}
          </div>

          {/* Typing here picks "other" by itself. */}
          <div className="flex flex-col gap-1">
            <Input.TextArea
              value={note}
              maxLength={MAX_REASON_LENGTH}
              autoSize={{ minRows: 3, maxRows: 6 }}
              placeholder={t("appointmentDetail.cancelReasonPlaceholder")}
              style={{ width: "100%" }}
              className="!bg-common-background !border-0 !rounded-lg !text-sm lg:!text-base"
              onFocus={() => setReason("reason3")}
              onChange={(event) => {
                setReason("reason3");
                setNote(event.target.value.slice(0, MAX_REASON_LENGTH));
              }}
            />
            {/* In the flow rather than antd's floating counter, so it never crowds the buttons. */}
            <span className="self-end text-xs font-['Inter'] text-common-subtext">
              {note.length}/{MAX_REASON_LENGTH}
            </span>
          </div>
        </div>
      }
    />
  );
}
